import { getMomentTz } from "@dexteel/mesf-core";
import type { Moment } from "moment-timezone";
import type {
	InspectionDashboardBucketKey,
	InspectionDashboardGrain,
} from "../../models/InspectionDashboard";

const moment = getMomentTz();

export const DATE_PRESET_VALUES = [
	"last7days",
	"last30days",
	"week",
	"month",
	"year",
] as const;

export type InspectionDatePreset = (typeof DATE_PRESET_VALUES)[number];

export const DATE_PRESETS: { value: InspectionDatePreset; label: string }[] = [
	{ value: "last7days", label: "Last 7 days" },
	{ value: "last30days", label: "Last 30 days" },
	{ value: "week", label: "Week" },
	{ value: "month", label: "Month" },
	{ value: "year", label: "Year" },
];

export const getPresetRange = (
	preset: InspectionDatePreset,
	now: Moment = moment(),
): { start: Moment; end: Moment } => {
	const today = now.clone().startOf("day");

	switch (preset) {
		case "last7days":
			return {
				start: today.clone().subtract(6, "days"),
				end: today.clone().endOf("day"),
			};
		case "last30days":
			return {
				start: today.clone().subtract(29, "days"),
				end: today.clone().endOf("day"),
			};
		case "week": {
			const start = today.clone().day(0);

			return { start, end: start.clone().add(6, "days").endOf("day") };
		}
		case "month":
			return {
				start: today.clone().startOf("month"),
				end: today.clone().endOf("month"),
			};
		case "year":
			return {
				start: today.clone().startOf("year"),
				end: today.clone().endOf("year"),
			};
	}
};

export const hasPresetShape = (
	preset: InspectionDatePreset,
	start: Moment | null,
	end: Moment | null,
) => {
	if (start === null || end === null) return false;

	const first = start.clone().startOf("day");
	const last = end.clone().startOf("day");
	const days = last.diff(first, "days") + 1;

	switch (preset) {
		case "year":
		case "month":
			return (
				first.isSame(first.clone().startOf(preset), "day") &&
				last.isSame(first.clone().endOf(preset), "day")
			);
		case "week":
			return first.day() === 0 && days === 7;
		case "last7days":
		case "last30days":
			return (
				days === (preset === "last7days" ? 7 : 30) &&
				last.isSame(moment(), "day")
			);
	}
};

export const shiftDateRange = (
	start: Moment,
	end: Moment,
	direction: 1 | -1,
	preset: InspectionDatePreset | null,
): { start: Moment; end: Moment } => {
	const first = start.clone().startOf("day");
	const last = end.clone().startOf("day");

	if (preset === "year" || preset === "month") {
		const moved = first.clone().add(direction, preset);

		return {
			start: moved.clone().startOf(preset),
			end: moved.clone().endOf(preset),
		};
	}

	const step = (last.diff(first, "days") + 1) * direction;

	return {
		start: first.clone().add(step, "days"),
		end: last.clone().add(step, "days").endOf("day"),
	};
};

export const isLatestDateRange = (end: Moment) =>
	end.isSameOrAfter(moment(), "day");

export const toDashboardWindow = (start: Moment, end: Moment) => ({
	startDate: start.clone().startOf("day").toDate(),
	endDate: end.clone().startOf("day").add(1, "day").toDate(),
});

const formatDayRange = (start: Moment, lastDay: Moment) => {
	if (start.isSame(lastDay, "day")) return start.format("MMM D, YYYY");

	return `${start.format(start.isSame(lastDay, "year") ? "MMM D" : "MMM D, YYYY")} - ${lastDay.format(
		lastDay.isSame(start, "month") ? "D, YYYY" : "MMM D, YYYY",
	)}`;
};

export const formatDateRange = (start: Moment | null, end: Moment | null) => {
	if (start === null && end === null) return "Any date";
	if (start === null) return `Up to ${end?.format("MMM D, YYYY")}`;
	if (end === null) return `From ${start.format("MMM D, YYYY")}`;

	return formatDayRange(start, end);
};

export const getInspectionEventLabel = (productionEvent: {
	EventId: number;
	SectionName: string;
	Start: Date | null;
	End: Date | null;
}): string => {
	const start = productionEvent.Start
		? moment(productionEvent.Start).format("MMM D HH:mm")
		: "";
	const end = productionEvent.End
		? moment(productionEvent.End).format("MMM D HH:mm")
		: "open";

	return `Event #${productionEvent.EventId} · ${productionEvent.SectionName} · ${start} - ${end}`;
};

export const getInspectionDashboardBucketLabel = (
	bucket: InspectionDashboardBucketKey,
	grain: InspectionDashboardGrain,
	isEvent: boolean,
): string => {
	if (bucket.ProductionDate && bucket.Shift) {
		const day = moment
			.utc(bucket.ProductionDate)
			.format(isEvent ? "MMM D" : "ddd D");

		return `${day} ${bucket.Shift}`;
	}

	const start = moment(bucket.BucketStartUtc);

	switch (grain) {
		case "day":
			return start.format("MMM D");
		case "month":
			return start.format("MMM YY");
		default:
			return start.format("MMM D HH:mm");
	}
};
